import {
  APP_ELEMENT_ID,
  EnumEntries,
  TitleOneDocEnum,
} from '../models/constants';
import {
  TechnicalRangeMetric,
  Maybe,
  Salutation,
  Title,
} from '../graphql/schema';
import { TestDriveStatus } from '../models/utilities/testdrive-status.enum';
export default class UtilsService {
  static capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  static canAccessContext(iframe: Window | null): boolean {
    try {
      return Boolean(iframe!.document);
    } catch (e) {
      return false;
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  static debounce(
    callback: (...args: any[]) => void,
    wait: number,
    immediate = false
  ): (...args: any) => void {
    let timeout = 0;
    return (...args: any) => {
      const callNow = immediate && !timeout;

      window.clearTimeout(timeout);
      const next = () => callback.apply(this, args);
      timeout = window.setTimeout(next, wait);

      if (callNow) {
        next();
      }
    };
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  static getStatusClass(status: Maybe<TestDriveStatus>): string {
    switch (status) {
      case TestDriveStatus.BOOKED: {
        return 'status-booking';
      }
      case TestDriveStatus.REQUESTED: {
        return 'status-request';
      }
      case TestDriveStatus.CANCELLED: {
        return 'status-cancelled';
      }
      case TestDriveStatus.DONE: {
        return 'status-done';
      }
      default: {
        return 'status-booking';
      }
    }
  }

  static formatTechnicalRangeMetric(
    information: Maybe<TechnicalRangeMetric>
  ): string {
    if (information && information.individual && information.unit) {
      return `${information.individual} ${information.unit}`;
    }
    return '-';
  }

  static getDOM = (windowFrame?: Window & typeof globalThis): any => {
    return windowFrame?.document || document;
  };

  static fetchResource = (
    url: string,
    insideShadow?: boolean
  ): Promise<HTMLScriptElement | HTMLLinkElement> => {
    return new Promise((resolve, reject) => {
      const pattern = /(css|js|)$/;
      let node: HTMLScriptElement | HTMLLinkElement;
      let selector: string;
      let type: string;
      let rel: string;

      if (!url) {
        reject(new Error('Missing resource URL'));
      } else {
        const ext = pattern.exec(url)![0];
        switch (ext) {
          case 'js':
            node = document.createElement('script');
            selector = 'src';
            type = 'text/javascript';
            break;
          case 'css':
            node = document.createElement('link');
            selector = 'href';
            type = 'text/css';
            rel = 'stylesheet';
            break;
          default:
            reject(new Error('Invalid extension type'));
            break;
        }
        const root = UtilsService.getDOM();
        const existing: any = root?.querySelector(`[${selector!}="${url}"]`);
        if (!existing) {
          node!.setAttribute(selector! as string, url);
          node!.setAttribute('type', type!);
          if (rel!) {
            node!.setAttribute('rel', rel!);
          }
          // eslint-disable-next-line no-unused-expressions
          insideShadow
            ? root?.appendChild(node!)
            : window.document.head.appendChild(node!);
          node!.addEventListener('load', () => {
            resolve(node);
          });
          node!.addEventListener('error', () => {
            reject(new Error('Error loading resource'));
          });
        } else {
          resolve(existing);
        }
      }
    });
  };

  static isDateValid(d: Date): boolean {
    return d instanceof Date && !Number.isNaN(d.getTime());
  }

  static outerHeight(el: HTMLElement): number {
    const style = getComputedStyle(el);
    return (
      el.getBoundingClientRect().height +
      parseFloat(style.marginTop.replace('px', '')) +
      parseFloat(style.marginBottom.replace('px', ''))
    );
  }

  static stripUndefinedValuesFromObject(obj: any): any {
    Object.keys(obj).forEach(
      (key) => obj[key] === undefined && delete obj[key]
    );
    return obj;
  }

  static getShadowRoot(applicationName: string): any {
    return (document as any).querySelector(applicationName).shadowRoot;
  }

  static closeAllAccordions(): void {
    UtilsService.getShadowRoot('otb-otbc')
      .querySelectorAll('wb-accordion-item')
      .forEach((accordion) => {
        accordion.removeAttribute('open');
      });
  }
  static isNull(value: any): boolean {
    return !value || value === 'null';
  }

  static compareObjects(first: any, last: any): any {
    if (
      !UtilsService.isNull(first) &&
      !UtilsService.isNull(last) &&
      typeof first === 'object' &&
      typeof last === 'object'
    ) {
      return (
        JSON.stringify(Object.entries(first).sort()) ===
        JSON.stringify(Object.entries(last).sort())
      );
    }
    return first === last;
  }

  static removeDuplicates(data: any[]): any[] {
    // we need to treat different when we're dealing with objects
    if (data.length > 0 && typeof data[0] === 'object') {
      return UtilsService.removeDuplicatesFromObject(data);
    }

    return [...new Set(data)];
  }

  static removeDuplicatesFromObject(data: any[]): any {
    const options: any[] = [];

    data.forEach((item) => {
      if (options.length > 0) {
        let shouldInsert = true;

        for (let index = 0; index < options.length; index += 1) {
          const preview: any = options[index];

          if (UtilsService.compareObjects(item, preview)) {
            shouldInsert = false;
          }
        }

        if (shouldInsert) {
          options.push(item);
        }
      } else {
        options.push(item);
      }
    });
    return options;
  }

  static cleanObject(obj: any): any {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value != null)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            // If the value is an array, return it as is after cleaning each element of the array.
            // This ensures that arrays maintain their structure and are not converted to objects.
            return [
              key,
              value.map((v) =>
                v === Object(v) ? UtilsService.cleanObject(v) : v
              ),
            ];
          } else {
            return [
              key,
              value === Object(value) ? UtilsService.cleanObject(value) : value,
            ];
          }
        })
    );
  }

  static removeEmptyStringsFromObject(obj: any): any {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value != '')
        .map(([key, value]) => [
          key,
          value === Object(value)
            ? UtilsService.removeEmptyStringsFromObject(value)
            : value,
        ])
    );
  }

  static cleanTypenames(obj: any): any {
    for (const property in obj) {
      if (
        typeof obj[property] === 'object' &&
        !(obj[property] instanceof File)
      ) {
        delete obj.property;
        const newData = UtilsService.cleanTypenames(obj[property]);
        obj[property] = newData;
      } else {
        if (property === '__typename') {
          delete obj[property];
        }
      }
    }
    return obj;
  }

  /**
   * Sets an object props value given a prop path (separated by dots) and a new value
   * Will recreate the object path if doesn't exist
   * @example
   * setNestedValueToObject({}, 'a.b', 2) will return {a: {b: 2}}
   * setNestedValueToObject({a: 'something'}, 'a.b', 2) will return {a: {b: 2}}
   * setNestedValueToObject({a: {b: 1}}, 'a.b', 2) will return {a: {b: 2}}
   * setNestedValueToObject({a: {b: 1}}, 'a.c', 2) will return {a: {b: 1, c: 2}}
   */
  static setNestedValueToObject(obj: any, propPath: string, value: any): any {
    const [head, ...rest] = propPath.split('.');
    return {
      ...obj,
      [head]: !rest.length // last prop
        ? value
        : UtilsService.setNestedValueToObject(
            obj[head] || {},
            rest.join('.'),
            value
          ),
    };
  }

  static getKeyByRegExp(object: any, regExp: RegExp): any {
    return Object.keys(object).find((key) => key.match(regExp));
  }

  static buildOneDocUrl(url: string, selectionData: any): string {
    let match;
    let parsed = url;
    // eslint-disable-next-line no-cond-assign
    while ((match = /\${([^}]*)}/g.exec(parsed)) !== null) {
      const [placeholder, property] = match;

      const value = selectionData[property];
      const data = encodeURIComponent(value);
      parsed = parsed.replace(placeholder, data);
    }

    return UtilsService.buildMappedOneDocURL(parsed);
  }

  static buildMappedOneDocURL(url: string): string {
    const splitedURL = url.split('/?');

    let mappedURL = '';
    mappedURL = UtilsService.removeUnspecifiedValuesForOneDoc(splitedURL[1]);
    mappedURL = UtilsService.mapSalutationForOneDoc(mappedURL);
    mappedURL = UtilsService.mapTitleForOneDoc(mappedURL);

    return `${splitedURL[0]}/?${mappedURL}`;
  }

  static removeUnspecifiedValuesForOneDoc(params: string): string {
    const urlParams = new URLSearchParams(params);

    // eslint-disable-next-line no-restricted-syntax
    for (const pair of urlParams) {
      if (pair[1] === 'undefined' || pair[1] === undefined) {
        urlParams.delete(String(pair[0]));
      }
    }

    return urlParams.toString();
  }

  static mapSalutationForOneDoc(params: string): string {
    const salutationEntries = UtilsService.getEntriesFromEnum(Salutation);
    const titleEntries = UtilsService.getEntriesFromEnum(Title);

    const salutations = [
      ...new Set([
        ...Object.values(titleEntries).map((item: any) => item.value),
        ...Object.values(salutationEntries).map((item: any) => item.value),
      ]),
    ];

    const urlParams = new URLSearchParams(params);

    const current = urlParams.get('cs');
    if (!current) {
      return params;
    }

    if (salutations.includes(current.toUpperCase())) {
      const newParams = params.replace(
        `cs=${current}`,
        `cs=${current.toUpperCase()}`
      );
      return newParams;
    }

    urlParams.delete('cs');

    return urlParams.toString();
  }

  static mapTitleForOneDoc(params: string): string {
    const titleOneDocEntries = UtilsService.getEntriesFromEnum(TitleOneDocEnum);

    const titlesOneDocKeys = [
      ...Object.values(titleOneDocEntries).map((item: any) => item.key),
    ];
    const titlesOneDocValues = [
      ...Object.values(titleOneDocEntries).map((item: any) => item.value),
    ];

    const urlParams = new URLSearchParams(params);

    const current = urlParams.get('cat');
    if (!current) {
      return params;
    }

    if (titlesOneDocKeys.includes(current.toUpperCase())) {
      const titleOneDocIndex = titlesOneDocKeys.findIndex(
        (key) => key === current.toUpperCase()
      );

      if (titleOneDocIndex >= 0) {
        urlParams.set('cat', titlesOneDocValues[titleOneDocIndex]);
      }

      return urlParams.toString();
    }

    urlParams.delete('cat');

    return urlParams.toString();
  }

  static getEntriesFromEnum(enumData: any): EnumEntries[] {
    const values: EnumEntries[] = Object.entries(enumData)
      // @ts-ignore
      .map(([k, v]) => ({ key: k, value: v }));

    return values;
  }

  static paramsToObject(params: string): any {
    const urlParams = new URLSearchParams(params);
    const entries = urlParams.entries();

    const result: {
      [key: string]: string;
    } = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of entries) {
      result[key] = value;
    }
    return result;
  }

  static removeKey(keyName: string, obj: any): void {
    for (const key in obj) {
      if (key === keyName) {
        delete obj[key];
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        UtilsService.removeKey(keyName, obj[key]);
      }
    }
  }

  static sortByAlphabeticalOrder(
    list: Array<any>,
    keyToCompare: string
  ): Array<any> {
    return list.sort((itemA, itemB) => {
      return itemA[keyToCompare]
        .toString()
        .toLowerCase()
        .localeCompare(itemB[keyToCompare].toString().toLowerCase());
    });
  }

  static isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  static findOTBIframe(): HTMLIFrameElement | null {
    let iframe: undefined | null | HTMLIFrameElement; // saved iframe reference
    if (iframe !== undefined) return iframe; // if iframe was already found just pass it
    iframe = null;
    if (!UtilsService.isInIframe()) return iframe; // if is not inside iframe may skip trying to find it
    const iframes = window.parent.document.getElementsByTagName('iframe');
    for (let i = 0, len = iframes.length; i < len; i += 1) {
      try {
        // avoid breaking when accessing forbidden frames
        if (
          document === iframes[i].contentDocument ||
          window.self === iframes[i].contentWindow
        ) {
          iframe = iframes[i]; // found iframe
          break;
        }
      } catch (error) {
        console.error(error);
      }
    }
    return iframe;
  }

  static getTopMostAllowedFrame(): Window {
    let frame = window.self;
    const otbFrame = UtilsService.findOTBIframe();
    if (
      UtilsService.canAccessContext(window.top) &&
      otbFrame?.id === `${APP_ELEMENT_ID}-cont`
    ) {
      // @ts-ignore
      frame = window.top;
    }
    return frame;
  }

  static getUrlSearchVariable(name: string): string | null {
    const { search } = UtilsService.getTopMostAllowedFrame().location;
    if (search) {
      const param = new URLSearchParams(search.substring(1)).get(name);
      return param ? decodeURIComponent(<string>param) : '';
    }
    return null;
  }
}
